/**
 * 设备标识。
 *
 * 契约把 `deviceId` 定义为**请求体字段**（4-64 字符，见 `DeviceIdSchema`），
 * 服务端不读任何设备头，所以它只能由前端生成并持久化。
 * 它不参与鉴权，只是一条风控/审计线索，因此不值得加密或加固——但必须稳定，
 * 否则同一台设备每次刷新都会被当成新设备，短信风控形同虚设。
 */

const DEVICE_ID_KEY = 'spirit.device_id';
const DEVICE_ID_MIN_LENGTH = 4;
const RANDOM_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const RANDOM_LENGTH = 24;

/**
 * `crypto.randomUUID()` 需要安全上下文（https 或 localhost）。
 * 用局域网 IP 调试时它不是安全上下文，所以必须有回退实现；
 * 回退只用 `Math.random`，因为这里要的是"不重复"，不是"不可预测"。
 */
function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `d_${crypto.randomUUID().replace(/-/g, '')}`;
  }
  let suffix = '';
  for (let index = 0; index < RANDOM_LENGTH; index += 1) {
    suffix += RANDOM_ALPHABET[Math.floor(Math.random() * RANDOM_ALPHABET.length)];
  }
  return `d_${suffix}`;
}

/**
 * 取（必要时创建）本设备的 id。
 *
 * 存储不可用时返回一个仅本次会话有效的 id：短信验证仍然能走通，
 * 只是每次刷新都会被当成新设备。这比因为隐私模式而完全无法登录要好。
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') {
    return 'd_server_side_placeholder';
  }
  try {
    const stored = window.localStorage.getItem(DEVICE_ID_KEY);
    if (stored !== null && stored.length >= DEVICE_ID_MIN_LENGTH) {
      return stored;
    }
    const created = randomId();
    window.localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch {
    return randomId();
  }
}
